import { calculateBMIImperial, calculateImperialWeightFromBMI } from "./bmi";

const RECOMMENDED_WEIGHT_LOWER_BOUND_BMI = 19;
const RECOMMENDED_WEIGHT_UPPER_BOUND_BMI = 25;
const RECOMMENDED_WEIGHT_LOWER_BOUND_LIMIT_LB = 70;
const RECOMMENDED_WEIGHT_UPPER_BOUND_LIMIT_LB = 60;

type RecommendedWeightRange = {
  lowerRangeRecommendedWeight?: string;
  upperRangeRecommendedWeight?: string;
};

/**
 * Calculates the recommended upper and lower bounds for ideal weight given
 * user weight and height
 *
 * @returns Object containing recommended upper and lower bounds for ideal
 * weight.
 */
export function calculateRecommendedWeightRange(
  heightFeetPortion?: number,
  heightInchesPortion?: number,
  weightLbs?: number,
): RecommendedWeightRange {
  // Return undefined range if for some reason height or weight is not defined
  if (!heightFeetPortion || !heightInchesPortion || !weightLbs) {
    return {
      lowerRangeRecommendedWeight: undefined,
      upperRangeRecommendedWeight: undefined,
    };
  }

  const currentBMI = calculateBMIImperial(
    heightFeetPortion,
    heightInchesPortion,
    weightLbs,
  );

  // Return undefined range if the user is already below the upper bound for
  // recommended weight
  if (currentBMI < RECOMMENDED_WEIGHT_UPPER_BOUND_BMI) {
    return {
      lowerRangeRecommendedWeight: undefined,
      upperRangeRecommendedWeight: undefined,
    };
  }

  let lowerRangeRecommendedWeight = calculateImperialWeightFromBMI(
    heightFeetPortion,
    heightInchesPortion,
    RECOMMENDED_WEIGHT_LOWER_BOUND_BMI,
  );
  let upperRangeRecommendedWeight = calculateImperialWeightFromBMI(
    heightFeetPortion,
    heightInchesPortion,
    RECOMMENDED_WEIGHT_UPPER_BOUND_BMI,
  );

  if (
    upperRangeRecommendedWeight <
    weightLbs - RECOMMENDED_WEIGHT_UPPER_BOUND_LIMIT_LB
  ) {
    lowerRangeRecommendedWeight =
      weightLbs - RECOMMENDED_WEIGHT_LOWER_BOUND_LIMIT_LB;
    upperRangeRecommendedWeight =
      weightLbs - RECOMMENDED_WEIGHT_UPPER_BOUND_LIMIT_LB;
  }

  return {
    lowerRangeRecommendedWeight: lowerRangeRecommendedWeight.toFixed(0),
    upperRangeRecommendedWeight: upperRangeRecommendedWeight.toFixed(0),
  };
}
